import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { environment } from "@env/environment";
import { ApiService } from "@services/api.service";
import { SessionService } from "@services/session.service";
import { AuthService } from "../auth.service";
import { Usuario } from "@models/usuario.model";
import { assignToModel } from "@shared/utils/fieldsMap.utils";
import Roles from "@shared/enums/roles.enum";

@Component({
  selector: "app-callback",
  templateUrl: "./callback.component.html",
  styles: []
})
export class CallbackComponent implements OnInit {
  statusMessage: string = "Procesando autenticación...";
  isError: boolean = false;
  showCloseSessionsButton: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private sessionService: SessionService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    // En modo desarrollo, siempre usar usuario fake
    if (environment.disableAuth) {
      this.statusMessage = "Creando sesión de desarrollo...";
      const fake = assignToModel(Usuario, {
        id_sesion: 'FAKE',
        rut_usuario: '11111111-1',
        nombres: 'Dev',
        ape_paterno: 'User',
        ape_materno: '',
        mail_usuario: 'dev@example.com',
        token: 'FAKE_TOKEN',
        creacion: new Date().toISOString(),
        expiracion: new Date(Date.now() + 24 * 3600 * 1000).toISOString(),
        aplicacion: 'FAKE',
        perfiles: [],
        roles: '1',
        cobertura: [],
        token_cu: '',
        permisos: { Funcionalidades: [] },
        terminologia: {},
        rolSeleccionado: 1 as Roles,
      });
      this.sessionService.setSesion(fake);
      setTimeout(async () => {
        this.router.navigate(["/inicio"]); 
        window.location.href = '/inicio';
      }, 500);
      return;
    }

    const code = this.route.snapshot.queryParamMap.get("code");
    const state = this.route.snapshot.queryParamMap.get("state");

    if (code && state) {
      this.authService.handleCallback(window.location.href)
        .then(async (user) => {
          if (typeof user.permisos === 'string') {
            try {
              let permisosStr = user.permisos;
              if (!permisosStr || permisosStr.trim() === '.') {
                user.permisos = { Funcionalidades: [] };
              } else {
                // Clean up malformed backend JSON where 'Acciones': is followed by '}' directly
                permisosStr = permisosStr.replace(/"Acciones"[\s\r\n]*:[\s\r\n]*}/g, '"Acciones": []}');
                let parsedPermisos = JSON.parse(permisosStr);
                if (parsedPermisos && parsedPermisos.data && parsedPermisos.data.Funcionalidades) {
                  user.permisos = parsedPermisos.data;
                } else if (parsedPermisos && parsedPermisos.Funcionalidades) {
                  user.permisos = parsedPermisos;
                } else {
                   user.permisos = { Funcionalidades: [] };
                }
              }
            } catch (e) {
              console.error('Error parsing permisos', e);
              user.permisos = { Funcionalidades: [] }; // default so it doesn't break later logic
            }
          }
          await this.sessionService.setSesion(user);
          
          this.statusMessage = "Autenticación correcta. Redirigiendo a Inicio...";
          
          setTimeout(() => {
            this.router.navigate(["/inicio"]);
          }, 1500);
        })
        .catch((error) => {
          this.statusMessage = "Error durante la autenticación con Clave Única.";
          console.error('HandleCallback error:', error);
          this.isError = true;
        });
      return;
    }

    const isAuth = this.sessionService.isAuthenticated();

    if (isAuth) {
      this.statusMessage = "Usuario ya autenticado. Redirigiendo...";
      setTimeout(() => {
        this.router.navigate(["/inicio"]);
      }, 1500);
      return;
    }

    const error = this.route.snapshot.queryParamMap.get("error");
    const uuid = this.route.snapshot.queryParamMap.get("uuid");

    if (error) {
      const sub = this.sessionService.sesion$.subscribe(sesion => {
        if (sesion) {
          sub.unsubscribe();
          this.router.navigate(['/inicio']);
        }
      });
      setTimeout(() => {
        sub.unsubscribe();
        if (!this.sessionService.isAuthenticated()) {
          console.error("Error recibido en callback:", error);
          this.statusMessage = error;
          this.isError = true;
          this.showCloseSessionsButton = true;
        }
      }, 600);
      return;
    }

    if (!uuid) {
      this.statusMessage = "Esperando parámetros de autenticación...";
      return;
    }

    const backendUrl = environment.backendUrl + '/' + environment.SaludIrrecuperableRecSesion;

    this.apiService.privatePostObservable<any>(backendUrl, { uuid }).subscribe({
      next: async (response) => {
        if (typeof response === "object") {
          try {
            const sesion = response.Sesion?.[0];
            if (!sesion) {
              this.statusMessage = "Error: Sesión no válida.";
              this.isError = true;
              return;
            }

            if (typeof sesion.permisos === 'string') {
              try {
                let permisosStr = sesion.permisos;
                if (!permisosStr || permisosStr.trim() === '.') {
                  sesion.permisos = { Funcionalidades: [] };
                } else {
                  // Clean up malformed backend JSON where 'Acciones': is followed by '}' directly
                  permisosStr = permisosStr.replace(/"Acciones"[\s\r\n]*:[\s\r\n]*}/g, '"Acciones": []}');
                  let parsedPermisos = JSON.parse(permisosStr);
                  
                  if (parsedPermisos && parsedPermisos.data && parsedPermisos.data.Funcionalidades) {
                    sesion.permisos = parsedPermisos.data;
                  } else if (parsedPermisos && parsedPermisos.Funcionalidades) {
                    sesion.permisos = parsedPermisos;
                  } else {
                    sesion.permisos = { Funcionalidades: [] };
                  }
                }
              } catch (e){
                 console.error("Error parsing backend permisos", e)
                 sesion.permisos = { Funcionalidades: [] }; // fall back to prevent crash
              }
            }
            await this.sessionService.setSesion(sesion);
            this.statusMessage = "Autenticación completada.";
            this.isError = false;

            setTimeout(() => {
              this.router.navigate(["/inicio"]);
            }, 500);
          } catch (e) {
            console.error("Error parsing response:", e);
            this.statusMessage = "Error al procesar respuesta.";
            this.isError = true;
          }
        } else {
          this.statusMessage = "Respuesta inválida del servidor.";
          this.isError = true;
        }
      },
      error: (err) => {
        console.error("Error al obtener sesión:", err);
        this.statusMessage = "Error al obtener sesión.";
        this.isError = true;
      },
    });
  }

  cerrarTodasLasSesiones(): void {
    const logoutUrl = environment.backendUrl + environment.logout;

    this.apiService.privatePostObservable<any>(logoutUrl, {}).subscribe({
      next: () => {
        this.statusMessage = "Todas las sesiones han sido cerradas.";
        this.isError = false;
        this.showCloseSessionsButton = false;
      },
      error: (err) => {
        console.error("Error al cerrar sesiones:", err);
        this.statusMessage = "Error al cerrar sesiones.";
      },
    });
  }
}
